import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CONTROL_BRIDGE_HELD_SURFACE_COPY,
  type ControlBridgeHeldSurfaceKey,
  type ControlBridgeQueueItem,
  type ControlBridgeViewModel,
} from '../deriveViewModel';
import { findQueueItemById, queueItemOrFallback, queueItemsFromViewModel } from '../detail/findQueueItemById';
import { DiagnosePersonalLayer } from '../diagnose/DiagnosePersonalLayer';
import type { KryspaceControlBridgeStructure } from '../../../types/kryspaceControlBridgeStructure';
import type { KryspaceRenderManifest } from '../../../types/kryspaceRenderManifest';
import type { ControlBridgeDetailSelection } from '../ControlBridgeDetailPanel';
import type { ResourceFilter, ScopeContext } from '../filters';
import { passesFilter } from '../filters';
import { scopeIdsEqual, queueItemMatchesScopeId } from '../../../utils/scopeIdPlane';
import { resolveMcDomainArtifactInstanceId } from '../detail/resolveArtifactInstanceId';
import { filterEligibleForRoutingContext, isAssignedToUser } from '../attentionRouting';
import type { PersonalLayerUserContext } from '../personalLayer/buildPersonalLayerViewModels';
import { sortByPriority } from '../queueHelpers';
import {
  buildPersonalLayerViewModels,
  PersonalLayerDriveProxy,
  PersonalLayerGuidanceProxy,
  type PersonalOverlayMode,
} from '../personalLayer';
import {
  EmptyState,
  FilterBar,
  NotAssessed,
  SectionLabel,
} from '../shared/SurfaceParts';
import { QuoteRequestIntake } from '../quoteRequest/QuoteRequestIntake';
import {
  bridgeSessionKindForSurface,
  bridgeSessionKindLabel,
} from '../bridgeSessionKind';
import { ExoPanelPersonalLayer } from '../exoPanel/ExoPanelPersonalLayer';
import { isExoSystemOnlySchema } from '../exoPanel/ExoUserResponseControls';
import type { ExoWorkDeeplink } from '../exoPanel/exoPanelWorkDeeplink';
import { MissionControlPersonalLayer } from '../missionControl/MissionControlPersonalLayer';
import { rankExoQueue } from '../derivation';
import { FlowStageBoard, resolveDriveFlowModel, resolveProcessFlowModel } from './FlowStageBoard';
import { buildDriveFlowViewModel } from '../personalLayer/driveLaneAggregation';
import { activeScopeKeys } from '../deriveSubdomainFlow';
import { expandDriveEnvelopeScopeKeys, driveScopeContextForRail } from '../structureScope';
import { filterFlowModelByPeriod, filterFlowModelByScope, itemFromFlowModel, parseDriveViewPeriod, parseRollupEnvelopeFromManifest } from '../derivation/flowStage';
import {
  countWatchOccupancy,
  occupancyFromRollupNodes,
  scopeBreadcrumb,
  watchChildNodesForScope,
} from '../structureSidebarUi';
import styles from '../ControlBridge.module.css';

/** Deep-link target for FE-SURF-1 held/retired tabs — not a second work queue. */
export const HeldSurfaceUnavailable: React.FC<{ surface: ControlBridgeHeldSurfaceKey }> = ({
  surface,
}) => {
  const copy = CONTROL_BRIDGE_HELD_SURFACE_COPY[surface];
  return (
    <div className={styles.cbHeldSurface} data-testid="cb-held-surface-unavailable" data-surface={surface}>
      <div className={styles.cbHeldSurfaceTitle}>{copy.title}</div>
      <p className={styles.cbHeldSurfaceBody}>{copy.body}</p>
    </div>
  );
};

/** Brief / Organizer row — open selected item in Exo when a focus handler is wired. */
function BriefOpenableItem({
  item,
  meta,
  onOpenInExo,
  showActorMarker = false,
  testIdPrefix = 'brief-open-exo',
}: {
  item: ControlBridgeQueueItem;
  meta: React.ReactNode;
  onOpenInExo?: (itemId: string) => void;
  showActorMarker?: boolean;
  testIdPrefix?: string;
}) {
  const canOpen = Boolean(onOpenInExo && item.itemId);
  return (
    <li
      className={[styles.briefListItem, canOpen ? styles.briefListItemOpenable : '']
        .filter(Boolean)
        .join(' ')}
      role={canOpen ? 'button' : undefined}
      tabIndex={canOpen ? 0 : undefined}
      data-testid={canOpen ? `${testIdPrefix}-${item.itemId}` : undefined}
      onClick={
        canOpen
          ? () => {
              onOpenInExo?.(item.itemId);
            }
          : undefined
      }
      onKeyDown={
        canOpen
          ? (event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              onOpenInExo?.(item.itemId);
            }
          : undefined
      }
    >
      {showActorMarker && item.actorMarker ? (
        <span
          className={styles.actorMarker}
          data-kind={item.actorMarker.kind}
          data-testid="actor-marker"
          title={`${item.actorMarker.kind === 'agent' ? 'Agent' : 'Person'}: ${item.actorMarker.label}`}
        >
          <span className={styles.actorMarkerGlyph} aria-hidden>
            {item.actorMarker.kind === 'agent' ? 'A' : 'P'}
          </span>
          <span className={styles.actorMarkerLabel}>{item.actorMarker.label}</span>
        </span>
      ) : null}
      <strong>{item.title}</strong>
      <span className={styles.briefListMeta}>{meta}</span>
    </li>
  );
}

function filterQueue(
  items: ControlBridgeQueueItem[],
  scope: string,
  resource: ResourceFilter,
  companyWide: boolean,
  scopeContext?: ScopeContext
): ControlBridgeQueueItem[] {
  const scoped = companyWide
    ? items
    : items.filter((item) => passesFilter(item, scope, resource, scopeContext));
  return sortByPriority(
    scoped.filter((item) => passesFilter(item, companyWide ? '__all' : scope, resource, scopeContext))
  );
}

export interface SurfaceProps {
  vm: ControlBridgeViewModel;
  manifest: KryspaceRenderManifest;
  structure: KryspaceControlBridgeStructure;
  scope: string;
  resource: ResourceFilter;
  scopeContext?: ScopeContext;
  scopeLabel: string;
  resourceLabel: string;
  onNavigate: (surface: string) => void;
  onClearResource?: () => void;
  onSelectDetail?: (selection: ControlBridgeDetailSelection) => void;
  onOpenItemDetail?: (item: ControlBridgeQueueItem, invokingLabel?: string) => void;
  /** Item click routed by resolveClickTarget (chat shelf or Exo focus). */
  onRoutedItemClick?: (item: ControlBridgeQueueItem, invokingLabel?: string) => void;
  /** Critical / explicit Exo focus without opening entity overlay. */
  onFocusExoItem?: (itemId: string) => void;
  onBlockedRouteToChat?: (item: ControlBridgeQueueItem, reason: string) => void;
  onOpenArtifactDetail?: (artifactInstanceId: string, invokingLabel: string) => void;
  onGoDoWork?: (item: ControlBridgeQueueItem, deeplink: ExoWorkDeeplink) => void;
  onScopeChange?: (scopeKey: string) => void;
  selectedDetailId?: string;
  personalLayerUser?: PersonalLayerUserContext;
  personalOverlayMode?: PersonalOverlayMode;
  onPersonalOverlayModeChange?: (mode: PersonalOverlayMode) => void;
  workspaceSlug?: string;
  onManifestRefresh?: () => void | Promise<void>;
  /** When set, Exo Panel selects this item once it appears in the filtered queue. */
  exoFocusedItemId?: string | null;
  onExoFocusedItemApplied?: () => void;
  issuesFocusItemId?: string | null;
  issuesRefreshToken?: number;
  issuesListOpen?: boolean;
  onOpenIssueItem?: (itemId: string, label: string) => void;
  /** Drive period selector → parent refetches attention-manifest with ``drive_period``. */
  onDrivePeriodChange?: (periodCode: string) => void;
}

export const MissionControlSurface: React.FC<SurfaceProps> = ({
  vm,
  structure,
  onOpenArtifactDetail,
}) => (
  <MissionControlPersonalLayer
    vm={vm}
    density="expanded"
    structure={structure}
    onOpenArtifactDetail={onOpenArtifactDetail}
  />
);

export const DriveSurface: React.FC<SurfaceProps> = ({
  vm,
  manifest,
  structure,
  scope,
  resource,
  scopeContext,
  scopeLabel,
  resourceLabel,
  onNavigate,
  onClearResource,
  onDrivePeriodChange,
  personalLayerUser,
  personalOverlayMode = 'system',
  onPersonalOverlayModeChange,
  onOpenArtifactDetail,
  onOpenItemDetail,
  onRoutedItemClick,
  onFocusExoItem,
  onScopeChange,
}) => {
  const driveScopeContext = useMemo(
    () => (scopeContext ? driveScopeContextForRail(scope, structure, scopeContext) : scopeContext),
    [scope, structure, scopeContext]
  );

  const personalLayer = useMemo(
    () =>
      buildPersonalLayerViewModels({
        vm,
        manifest,
        structure,
        scope,
        resource,
        scopeContext: driveScopeContext,
        overlayMode: personalOverlayMode,
        user: personalLayerUser,
      }),
    [vm, manifest, structure, scope, resource, driveScopeContext, personalOverlayMode, personalLayerUser]
  );

  const queueItems = useMemo(() => queueItemsFromViewModel(vm), [vm]);
  const driveHasOpsSession = personalLayer.drive.hasOperationalSession !== false;
  const driveFlow = useMemo(
    () =>
      driveHasOpsSession && personalLayer.drive.subdomains.length
        ? buildDriveFlowViewModel(personalLayer.drive)
        : null,
    [personalLayer.drive, driveHasOpsSession]
  );
  const flowModel = useMemo(() => {
    // Orlin R10: without an Operational bridge session, do not paint envelope as Drive.
    if (!driveHasOpsSession) return null;
    const resolved = resolveDriveFlowModel(vm, driveFlow, queueItems);
    if (!resolved) return null;
    const scoped = filterFlowModelByScope(
      resolved,
      expandDriveEnvelopeScopeKeys(scope, structure, activeScopeKeys(scope, driveScopeContext))
    );
    const viewPeriod = parseDriveViewPeriod(manifest);
    const filtered = filterFlowModelByPeriod(scoped, viewPeriod);
    if (viewPeriod && !filtered.period) {
      return { ...filtered, period: viewPeriod };
    }
    return filtered;
  }, [
    vm,
    driveFlow,
    queueItems,
    scope,
    driveScopeContext,
    manifest,
    structure,
    driveHasOpsSession,
  ]);

  const resolveDriveItem = useCallback(
    (itemId: string): ControlBridgeQueueItem => {
      const stub = itemFromFlowModel(flowModel, itemId);
      return (
        stub ??
        queueItemOrFallback(queueItems, itemId, {
          title: itemId,
          statusPhrase: '',
        })
      );
    },
    [flowModel, queueItems]
  );

  const openDriveArtifactDetail = onOpenArtifactDetail
    ? (artifactInstanceId: string) => onOpenArtifactDetail(artifactInstanceId, 'Drive')
    : undefined;

  /** Ordinary / warning item click → Chat shelf (U4). Overlay stays on explicit Details only. */
  const openDriveItemChat = onRoutedItemClick
    ? (itemId: string) => {
        onRoutedItemClick(resolveDriveItem(itemId), 'Drive');
      }
    : onOpenItemDetail
      ? (itemId: string) => {
          onOpenItemDetail(resolveDriveItem(itemId), 'Drive');
        }
      : undefined;

  const openDriveExo = onNavigate
    ? (itemId: string) => {
        onNavigate('exopanel');
        if (onFocusExoItem) {
          onFocusExoItem(itemId);
          return;
        }
        if (onRoutedItemClick) {
          const item = resolveDriveItem(itemId);
          onRoutedItemClick(
            { ...item, blockerClass: item.blockerClass ?? 'critical' },
            'Drive · Exo'
          );
          return;
        }
        if (onOpenItemDetail) {
          onOpenItemDetail(resolveDriveItem(itemId), 'Drive · Exo');
        }
      }
    : undefined;

  const envelopeOwnsBoard = flowModel?.source === 'envelope';
  const crumbs = useMemo(
    () => scopeBreadcrumb(scope, structure, { fallbackLabel: scopeLabel }),
    [scope, structure, scopeLabel]
  );
  const watchStrips = useMemo(() => {
    const rows = watchChildNodesForScope(structure, scope, {
      lens: vm.roleLens,
      roleId: vm.roleLens?.roleId,
    });
    const rollup = parseRollupEnvelopeFromManifest(manifest);
    return rows.map((row) => {
      const occupancy = countWatchOccupancy(row.key, personalLayer.drive.artifacts, driveScopeContext);
      const metrics = rollup ? occupancyFromRollupNodes(row.key, rollup.nodes) : null;
      return {
        ...row,
        ...(occupancy ?? {}),
        ...(metrics ?? {}),
      };
    });
  }, [structure, scope, vm.roleLens, personalLayer.drive.artifacts, driveScopeContext, manifest]);

  return (
    <div className={styles.surfaceStackWide}>
      <p className={styles.surfaceSubtitle} data-testid="cb-session-kind">
        {bridgeSessionKindLabel(bridgeSessionKindForSurface('drive')!)}
      </p>
      <QuoteRequestIntake />
      <nav
        className={styles.driveScopeBreadcrumb}
        aria-label="Drive scope"
        data-testid="drive-scope-breadcrumb"
      >
        {crumbs.map((crumb, index) => {
          const last = index === crumbs.length - 1;
          return (
            <React.Fragment key={`${crumb.key}-${index}`}>
              {index > 0 ? (
                <span className={styles.driveScopeBreadcrumbSep} aria-hidden>
                  ▸
                </span>
              ) : null}
              {last || !onScopeChange ? (
                <span
                  className={last ? styles.driveScopeBreadcrumbCurrent : undefined}
                  aria-current={last ? 'location' : undefined}
                  data-scope-key={crumb.key}
                >
                  {crumb.label}
                </span>
              ) : (
                <button
                  type="button"
                  className={styles.driveScopeBreadcrumbBtn}
                  data-scope-key={crumb.key}
                  onClick={() => onScopeChange(crumb.key)}
                >
                  {crumb.label}
                </button>
              )}
            </React.Fragment>
          );
        })}
      </nav>
      {flowModel ? (
        <FlowStageBoard
          flowModel={flowModel}
          title="What's flowing"
          scopeLabel={scopeLabel}
          showPeriodCaption={false}
          onStageClick={() => undefined}
          onItemClick={openDriveItemChat}
          onOpenExo={openDriveExo}
        />
      ) : null}
      {watchStrips.length ? (
        <section data-testid="drive-watch-strips">
          <SectionLabel>Watch</SectionLabel>
          <div className={styles.driveWatchStrips}>
            {watchStrips.map((strip) => {
              const occupancy =
                strip.inFlow != null || strip.blocked != null
                  ? `${strip.inFlow ?? 0} moving${strip.blocked ? ` · ${strip.blocked} held` : ''}`
                  : null;
              const metrics =
                strip.revenueActual != null || strip.costActual != null
                  ? `${occupancy ? ' · ' : ''}${
                      strip.revenueActual != null ? `rev ${strip.revenueActual}` : ''
                    }${
                      strip.revenueActual != null && strip.costActual != null ? ' · ' : ''
                    }${strip.costActual != null ? `cost ${strip.costActual}` : ''}`
                  : null;
              const meta = `${occupancy ?? ''}${metrics ?? ''}` || null;
              const body = (
                <>
                  <span>
                    {strip.label}
                    {meta ? (
                      <span className={styles.driveWatchStripMeta}>{meta}</span>
                    ) : null}
                  </span>
                  <span className={styles.driveWatchStripTag}>Watch</span>
                </>
              );
              return onScopeChange ? (
                <button
                  key={strip.key}
                  type="button"
                  className={styles.driveWatchStrip}
                  onClick={() => onScopeChange(strip.key)}
                >
                  {body}
                </button>
              ) : (
                <div key={strip.key} className={styles.driveWatchStrip}>
                  {body}
                </div>
              );
            })}
          </div>
        </section>
      ) : null}
      <section>
        {envelopeOwnsBoard ||
        personalLayer.drive.subdomains.length > 0 ||
        personalLayer.drive.hasOperationalSession === false ? (
          <PersonalLayerDriveProxy
            model={personalLayer.drive}
            overlayMode={personalOverlayMode}
            scopeLabel={scopeLabel}
            resourceLabel={resourceLabel}
            onClearResource={onClearResource}
            recommendedSurface={vm.attentionRouting?.recommendedSurface}
            onNavigate={onNavigate}
            onOpenArtifactDetail={openDriveArtifactDetail}
            onOpenItemDetail={openDriveItemChat}
            contextOnly={
              envelopeOwnsBoard && !(personalLayer.drive.orderFlowStages?.length)
            }
            onDrivePeriodChange={onDrivePeriodChange}
          />
        ) : (
          <NotAssessed label="Subdomain flow for selected scope" />
        )}
      </section>
    </div>
  );
};

export const GuidanceSurface: React.FC<SurfaceProps> = ({
  vm,
  manifest,
  structure,
  scope,
  resource,
  scopeContext,
  scopeLabel,
  resourceLabel,
  onNavigate,
  onClearResource,
  personalLayerUser,
  personalOverlayMode = 'system',
  onPersonalOverlayModeChange,
  onOpenArtifactDetail,
  onOpenItemDetail,
  onRoutedItemClick,
  workspaceSlug,
  onManifestRefresh,
}) => {
  const personalLayer = useMemo(
    () =>
      buildPersonalLayerViewModels({
        vm,
        manifest,
        structure,
        scope,
        resource,
        scopeContext,
        overlayMode: personalOverlayMode,
        user: personalLayerUser,
      }),
    [vm, manifest, structure, scope, resource, scopeContext, personalOverlayMode, personalLayerUser]
  );

  const queueItems = useMemo(() => queueItemsFromViewModel(vm), [vm]);

  return (
    <div className={styles.surfaceStackWide}>
      <FilterBar
        scopeLabel={scopeLabel}
        resourceLabel={resourceLabel}
        onClearResource={onClearResource}
        sessionKindLabel={bridgeSessionKindLabel(bridgeSessionKindForSurface('guidance')!)}
      />

      <section>
        <SectionLabel>Plan</SectionLabel>
        {personalLayer.guidance ? (
          <PersonalLayerGuidanceProxy
            model={personalLayer.guidance}
            overlayMode={personalOverlayMode}
            structure={structure}
            queueItems={queueItems}
            workspaceSlug={workspaceSlug}
            onManifestRefresh={onManifestRefresh}
            onOpenArtifactDetail={onOpenArtifactDetail}
            onOpenItemDetail={onOpenItemDetail}
            onSelectItem={onRoutedItemClick}
            onOpenSurface={onNavigate}
          />
        ) : (
          <div className={styles.emptyState}>
            <p>No waypoint plan in the manifest yet.</p>
          </div>
        )}
      </section>

    </div>
  );
};

export const DiagnoseSurface: React.FC<SurfaceProps> = ({
  vm,
  structure,
  scope,
  resource,
  scopeContext,
  scopeLabel,
  resourceLabel,
  onNavigate,
  onClearResource,
  onSelectDetail,
  personalLayerUser,
  workspaceSlug,
  issuesFocusItemId,
  issuesRefreshToken,
  issuesListOpen,
  onOpenIssueItem,
}) => (
  <DiagnosePersonalLayer
    vm={vm}
    structure={structure}
    scope={scope}
    resource={resource}
    scopeContext={scopeContext}
    scopeLabel={scopeLabel}
    resourceLabel={resourceLabel}
    onClearResource={onClearResource}
    onNavigate={onNavigate}
    onSelectDetail={onSelectDetail}
    personalLayerUser={personalLayerUser}
    workspaceSlug={workspaceSlug}
    issuesFocusItemId={issuesFocusItemId}
    issuesRefreshToken={issuesRefreshToken}
    issuesListOpen={issuesListOpen}
    onOpenIssueItem={onOpenIssueItem}
  />
);

export const ExoPanelSurface: React.FC<SurfaceProps> = ({
  vm,
  scope,
  resource,
  scopeContext,
  scopeLabel,
  resourceLabel,
  onClearResource,
  onOpenItemDetail,
  onFocusExoItem,
  onRoutedItemClick,
  onBlockedRouteToChat,
  onGoDoWork,
  personalLayerUser,
  personalOverlayMode = 'system',
  workspaceSlug,
  onManifestRefresh,
  exoFocusedItemId = null,
  onExoFocusedItemApplied,
}) => {
  /** Survives clearing ``exoFocusedItemId`` after apply so Me/lens injection stays. */
  const [heldFocusItemId, setHeldFocusItemId] = useState<string | null>(null);

  useEffect(() => {
    if (exoFocusedItemId) {
      setHeldFocusItemId(exoFocusedItemId);
    }
  }, [exoFocusedItemId]);

  // Domain selection must not gate ExoPanel membership (workspace queue). Do not
  // clear a held focus pin when sidebar scope changes — membership is company-wide.
  const clearFocusPin = useCallback(() => {
    setHeldFocusItemId(null);
  }, []);

  // Domain selection must not gate ExoPanel membership (workspace queue). Resource /
  // Me overlay still applies. Row filterScope remains for Drive / detail.
  const filtered = filterEligibleForRoutingContext(
    vm.eligibleWork,
    'company',
    resource,
    scopeContext,
    personalOverlayMode,
    personalLayerUser
  );
  const personalFiltered =
    personalOverlayMode === 'me' ? filtered.filter((item) => !isExoSystemOnlySchema(item)) : filtered;
  const ranked = rankExoQueue(personalFiltered);
  const focusInjectId = exoFocusedItemId ?? heldFocusItemId;
  // Keep MC next-move / sidebar focus visible when Me/resource filters exclude it.
  // Search the full VM catalog (waypoints, blockedWork, …) — not only eligibleWork/openIssues —
  // otherwise Next Waypoint / Critical Blocker clicks fall through to queue[0] (critical action).
  const queue = useMemo(() => {
    if (!focusInjectId) return ranked;
    if (findQueueItemById(ranked, focusInjectId)) return ranked;
    const catalog = queueItemsFromViewModel(vm);
    let focused = findQueueItemById(catalog, focusInjectId);
    if (!focused && vm.mcNextMove) {
      const nextMoveStubs: ControlBridgeQueueItem[] = [
        vm.mcNextMove.nextWaypoint,
        vm.mcNextMove.criticalAction,
        vm.mcNextMove.criticalBlocker,
      ]
        .filter((c): c is NonNullable<typeof c> => Boolean(c?.itemId))
        .map((c) => ({
          itemId: c.itemId!,
          title: c.title,
          statusPhrase: c.statusPhrase || 'open',
          priority: c.priority,
        }));
      focused = findQueueItemById(nextMoveStubs, focusInjectId);
    }
    if (!focused) return ranked;
    if (personalOverlayMode === 'me' && isExoSystemOnlySchema(focused)) {
      return ranked;
    }
    return [focused, ...ranked];
  }, [ranked, focusInjectId, vm, personalOverlayMode]);
  const messageItems = useMemo(() => {
    const seen = new Set<string>();
    const out: typeof vm.openIssues = [];
    for (const item of [...vm.openIssues, ...vm.recommendedFocus]) {
      const id = String(item.itemId || '').trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(item);
    }
    return out;
  }, [vm.openIssues, vm.recommendedFocus]);
  const messageAttentionCount = messageItems.length;
  const exoScopeLabel = 'Workspace';

  return (
    <div className={styles.surfaceStackWide}>
      <ExoPanelPersonalLayer
        queue={queue}
        blockedCount={vm.blockedActivityCount}
        messageAttentionCount={messageAttentionCount}
        messageItems={messageItems}
        calendar={vm.exoPanelCalendar}
        scopeLabel={exoScopeLabel}
        resourceLabel={resourceLabel}
        onClearResource={onClearResource}
        onOpenItemDetail={onOpenItemDetail}
        onFocusExoItem={onFocusExoItem}
        onRoutedItemClick={onRoutedItemClick}
        onBlockedRouteToChat={onBlockedRouteToChat}
        onGoDoWork={onGoDoWork}
        workspaceSlug={workspaceSlug}
        onManifestRefresh={onManifestRefresh}
        focusedItemId={exoFocusedItemId}
        onFocusedItemApplied={onExoFocusedItemApplied}
        onClearFocusPin={clearFocusPin}
      />
    </div>
  );
};

/** Process Flow — hierarchy roll-up (FE-SURF Process Flow). */
export const ProcessFlowSurface: React.FC<SurfaceProps> = (props) => {
  const { vm, onOpenItemDetail, onRoutedItemClick, onFocusExoItem, onNavigate } = props;
  const queueItems = useMemo(() => queueItemsFromViewModel(vm), [vm]);
  const personalLayer = useMemo(
    () =>
      buildPersonalLayerViewModels({
        vm,
        manifest: props.manifest,
        structure: props.structure,
        scope: props.scope,
        resource: props.resource,
        scopeContext: props.scopeContext,
        overlayMode: props.personalOverlayMode ?? 'system',
        user: props.personalLayerUser,
      }),
    [
      vm,
      props.manifest,
      props.structure,
      props.scope,
      props.resource,
      props.scopeContext,
      props.personalOverlayMode,
      props.personalLayerUser,
    ]
  );
  const driveFlow = useMemo(
    () => (personalLayer.drive.subdomains.length ? buildDriveFlowViewModel(personalLayer.drive) : null),
    [personalLayer.drive]
  );
  const flowModel = useMemo(
    () => resolveProcessFlowModel(driveFlow, queueItems),
    [driveFlow, queueItems]
  );

  if (!flowModel) {
    return (
      <div className={styles.surfaceStackWide}>
        <EmptyState label="Process Flow stages" />
      </div>
    );
  }

  return (
    <div className={styles.surfaceStackWide}>
      <p className={styles.surfaceSubtitle}>How does the hierarchy roll up?</p>
      <FlowStageBoard
        flowModel={flowModel}
        title="Process Flow"
        scopeLabel={props.scopeLabel}
        onItemClick={
          onRoutedItemClick || onOpenItemDetail
            ? (itemId) => {
                const item = queueItemOrFallback(queueItems, itemId, {
                  title: itemId,
                  statusPhrase: '',
                });
                (onRoutedItemClick ?? onOpenItemDetail)?.(item, 'Process Flow');
              }
            : undefined
        }
        onOpenExo={
          onNavigate
            ? (itemId) => {
                onNavigate('exopanel');
                if (onFocusExoItem) {
                  onFocusExoItem(itemId);
                  return;
                }
                if (onRoutedItemClick || onOpenItemDetail) {
                  const item = queueItemOrFallback(queueItems, itemId, {
                    title: itemId,
                    statusPhrase: '',
                  });
                  (onRoutedItemClick ?? onOpenItemDetail)?.(item, 'Process Flow · Exo');
                }
              }
            : undefined
        }
      />
    </div>
  );
};

/** Daily Brief — today's picture for the active lens. */
export const DailyBriefSurface: React.FC<SurfaceProps> = ({
  vm,
  scope,
  resource,
  scopeContext,
  scopeLabel,
  resourceLabel,
  onFocusExoItem,
}) => {
  const focus = useMemo(() => {
    const pool = [...vm.recommendedFocus, ...vm.eligibleWork].filter((item) =>
      passesFilter(item, scope, resource, scopeContext)
    );
    // Dedupe by itemId (recommendedFocus often overlaps eligibleWork).
    const seen = new Set<string>();
    const unique: ControlBridgeQueueItem[] = [];
    for (const item of pool) {
      if (!item.itemId || seen.has(item.itemId)) continue;
      seen.add(item.itemId);
      unique.push(item);
    }
    return sortByPriority(unique).slice(0, 8);
  }, [vm.recommendedFocus, vm.eligibleWork, scope, resource, scopeContext]);

  return (
    <div className={styles.surfaceStackWide} data-testid="daily-brief-surface">
      <p className={styles.surfaceSubtitle}>Today&apos;s picture for this lens.</p>
      <SectionLabel>Scope</SectionLabel>
      <p className={styles.briefMeta}>
        {scopeLabel}
        {resourceLabel ? ` · ${resourceLabel}` : ''}
      </p>
      <SectionLabel>Focus now</SectionLabel>
      {focus.length === 0 ? (
        <EmptyState label="Daily Brief focus" />
      ) : (
        <ul className={styles.briefList}>
          {focus.map((item) => (
            <BriefOpenableItem
              key={item.itemId}
              item={item}
              showActorMarker
              onOpenInExo={onFocusExoItem}
              meta={
                <>
                  {item.statusPhrase}
                  {item.priority ? ` · ${item.priority}` : ''}
                </>
              }
            />
          ))}
        </ul>
      )}
    </div>
  );
};

/** Personal Organizer — my load and watch strips. */
export const PersonalOrganizerSurface: React.FC<SurfaceProps> = ({
  vm,
  personalLayerUser,
  scope,
  resource,
  scopeContext,
  onFocusExoItem,
}) => {
  // Resource bar owns filtering: All = full scoped queue; person/type chip = that slice.
  const load = useMemo(
    () =>
      vm.eligibleWork.filter((item) => passesFilter(item, scope, resource, scopeContext)),
    [vm.eligibleWork, scope, resource, scopeContext]
  );

  const mine = useMemo(() => {
    const ref =
      personalLayerUser?.resourceProfileRef?.trim() || personalLayerUser?.id?.trim() || undefined;
    const label = personalLayerUser?.label?.trim();
    if (!ref && !label) return [];
    return load.filter((item) => {
      if (isAssignedToUser(item, ref)) return true;
      if (label && item.assignedResourceLabel?.trim() === label) return true;
      if (ref && item.res?.trim().toLowerCase() === ref.replace(/^person:/i, '').toLowerCase()) {
        return true;
      }
      return false;
    });
  }, [load, personalLayerUser?.resourceProfileRef, personalLayerUser?.id, personalLayerUser?.label]);

  const watch = useMemo(() => {
    if (!vm.roleLens) return [];
    return load.filter((item) => {
      const domain = item.domain ?? item.filterScope;
      if (!domain) return false;
      const tier = vm.roleLens?.domains.find((d) =>
        scopeIdsEqual(d.domainRef, domain)
      )?.tier;
      return tier === 'watch';
    });
  }, [load, vm.roleLens]);

  const showMineStrip = resource === 'all' && mine.length > 0;
  const primaryLabel = resource === 'all' ? 'All load' : 'Filtered load';

  return (
    <div className={styles.surfaceStackWide} data-testid="personal-organizer-surface">
      <p className={styles.surfaceSubtitle}>My load and offload.</p>
      {showMineStrip ? (
        <>
          <SectionLabel>Assigned to me</SectionLabel>
          <ul className={styles.briefList}>
            {mine.map((item) => (
              <BriefOpenableItem
                key={`mine-${item.itemId}`}
                item={item}
                onOpenInExo={onFocusExoItem}
                testIdPrefix="organizer-mine-open-exo"
                meta={item.statusPhrase}
              />
            ))}
          </ul>
        </>
      ) : null}
      <SectionLabel>{primaryLabel}</SectionLabel>
      {load.length === 0 ? (
        <EmptyState label={resource === 'all' ? 'organizer load' : 'filtered personal load'} />
      ) : (
        <ul className={styles.briefList}>
          {load.map((item) => (
            <BriefOpenableItem
              key={item.itemId}
              item={item}
              onOpenInExo={onFocusExoItem}
              testIdPrefix="organizer-load-open-exo"
              meta={
                <>
                  {item.statusPhrase}
                  {item.assignedResourceLabel ? ` · ${item.assignedResourceLabel}` : ''}
                </>
              }
            />
          ))}
        </ul>
      )}
      <SectionLabel>Watch</SectionLabel>
      {watch.length === 0 ? (
        <p className={styles.briefMeta}>No watch-domain items (RoleLens watch empty or provisional).</p>
      ) : (
        <ul className={styles.briefList}>
          {watch.map((item) => (
            <BriefOpenableItem
              key={`watch-${item.itemId}`}
              item={item}
              onOpenInExo={onFocusExoItem}
              testIdPrefix="organizer-watch-open-exo"
              meta={item.domain ?? item.filterScope}
            />
          ))}
        </ul>
      )}
      {scopeContext ? null : null}
    </div>
  );
};

/** Client status — Customer domain queue (SPA crosswalk X6). Falls back to title heuristic only when no Customer CA is on structure. */
export const ClientStatusSurface: React.FC<SurfaceProps> = ({ vm, structure }) => {
  const customerScope = useMemo(
    () => resolveMcDomainArtifactInstanceId(structure, 'customer'),
    [structure]
  );

  const delivery = useMemo(() => {
    const leak = (item: ControlBridgeQueueItem) => {
      if (item.issueType || item.issueSummary) return true;
      const type = `${(item as { artifactTypeRef?: string }).artifactTypeRef ?? ''}`.toLowerCase();
      const hay = `${item.title} ${type}`.toLowerCase();
      if (type.includes('issue') || type.includes('decision') || type.includes('convention')) {
        return true;
      }
      return /issue|convention|decision record|engine defect/.test(hay);
    };
    const rows = vm.eligibleWork.filter((item) => !leak(item));
    if (customerScope) {
      return rows.filter((item) => queueItemMatchesScopeId(item, customerScope));
    }
    return rows.filter((item) => {
      const hay = `${item.domain ?? ''} ${item.filterScope ?? ''} ${item.title}`.toLowerCase();
      return /delivery|order|ship/.test(hay);
    });
  }, [vm.eligibleWork, customerScope]);

  return (
    <div className={styles.surfaceStackWide} data-testid="client-status-surface">
      <p className={styles.surfaceSubtitle}>Where is my delivery?</p>
      <p className={styles.briefMeta} role="note">
        {customerScope
          ? 'Read-only Customer domain view. Model edits and Exo transitions are not available here.'
          : 'Read-only provisional view (no Customer domain CA on structure). Model edits and Exo transitions are not available here.'}
      </p>
      {delivery.length === 0 ? (
        <EmptyState label="Customer domain items" />
      ) : (
        <ul className={styles.briefList}>
          {delivery.map((item) => (
            <li key={item.itemId} className={styles.briefListItem}>
              <strong>{item.title}</strong>
              <span className={styles.briefListMeta}>{item.statusPhrase}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
